import React, { useState, useRef, useCallback } from 'react';
import { X, Paperclip, Trash2 } from 'lucide-react';
import { Button } from './ui/button';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'application/zip',
  'application/x-zip-compressed'
];
const ACCEPTED_EXTENSIONS = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip';

interface SessionFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => Promise<void>;
  submitting: boolean;
  error: string | null;
  /** True while a call is active: feedback is tied to that session (no title,
   *  `comment` field) rather than the tenant-wide inbox (`title` + `description`). */
  isSessionScoped: boolean;
}

const SessionFeedbackModal: React.FC<SessionFeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  submitting,
  error,
  isSessionScoped
}) => {
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndAddFiles = useCallback((newFiles: FileList | File[]) => {
    setFileError(null);
    const validated: File[] = [];
    for (const file of Array.from(newFiles)) {
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        setFileError(`"${file.name}" has an unsupported file type.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError(`"${file.name}" exceeds the 10 MB size limit.`);
        return;
      }
      validated.push(file);
    }
    setFiles(prev => [...prev, ...validated]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndAddFiles(e.dataTransfer.files);
  }, [validateAndAddFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      validateAndAddFiles(e.target.files);
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() || (!isSessionScoped && !title.trim())) return;

    const formData = new FormData();
    if (isSessionScoped) {
      formData.append('comment', comment.trim());
    } else {
      formData.append('title', title.trim());
      formData.append('description', comment.trim());
    }
    files.forEach(file => formData.append('attachments', file));

    await onSubmit(formData);
    // Reset form on success — onSubmit throws on error so this only runs when successful
    setTitle('');
    setComment('');
    setFiles([]);
    setFileError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    setTitle('');
    setComment('');
    setFiles([]);
    setFileError(null);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[rgba(28,27,24,0.5)]" onClick={handleClose} />

      {/* Modal panel */}
      <div className="relative modal-panel w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h2 className="heading-4">
            {isSessionScoped ? 'Session Feedback' : 'Feedback'}
          </h2>
          <Button variant="tertiary" onClick={handleClose} disabled={submitting}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {!isSessionScoped && (
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Title <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Brief summary of your feedback"
                required
                className="w-full border border-ink-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-signature focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Comment <span className="text-error">*</span>
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Share your feedback about this session..."
              rows={4}
              required
              className="w-full border border-ink-border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-signature focus:border-transparent"
            />
          </div>

          {/* Drag-and-drop zone */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Attachments <span className="text-xs text-ink-muted">(optional, 10 MB max per file)</span>
            </label>
            <div
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-colors ${
                isDragging ? 'border-signature bg-signature-tint' : 'border-ink-border hover:border-ink-muted'
              }`}
            >
              <Paperclip className="w-5 h-5 mx-auto text-ink-muted mb-1" />
              <p className="text-xs text-ink-muted">
                Drag files here or <span className="text-signature underline">browse</span>
              </p>
              <p className="text-xs text-ink-muted mt-0.5">Images, PDF, Word, Excel, TXT, ZIP</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_EXTENSIONS}
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          </div>

          {fileError && (
            <p className="text-xs text-error">{fileError}</p>
          )}

          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((file, index) => (
                <li key={index} className="flex items-center justify-between text-xs text-ink-secondary bg-surface-subtle rounded px-2 py-1">
                  <span className="truncate mr-2">{file.name}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-ink-muted">{formatFileSize(file.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-ink-muted hover:text-error"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {error && (
            <p className="text-xs text-ink bg-error-tint border border-error rounded px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !comment.trim() || (!isSessionScoped && !title.trim())}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionFeedbackModal;
